import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import Home from './pages/Home/Home';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { ComparisonProvider } from './context/ComparisonContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { AdminProvider } from './context/AdminContext';
import { WishlistProvider } from './context/WishlistContext';
import { OrderProvider } from './context/OrderContext';
import { CourierProvider } from './context/CourierContext';
import { ToastProvider } from './components/Toast/ToastContainer';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './utils/queryClient';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import AdminRoute from './components/ProtectedRoute/AdminRoute';
import CourierRoute from './components/ProtectedRoute/CourierRoute';
import './App.css';

// Lazy load route components for code splitting
const Shop = lazy(() => import('./pages/Shop/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart/Cart'));
const Checkout = lazy(() => import('./pages/Checkout/Checkout'));
const Category = lazy(() => import('./pages/Category/Category'));
const Account = lazy(() => import('./pages/Account/Account'));
const Comparison = lazy(() => import('./pages/Comparison/Comparison'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation/OrderConfirmation'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));
const ServerError = lazy(() => import('./pages/ServerError/ServerError'));
const Admin = lazy(() => import('./pages/Admin/Admin'));
const CourierLogin = lazy(() => import('./pages/Courier/CourierLogin'));
const CourierDashboard = lazy(() => import('./pages/Courier/CourierDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const Returns = lazy(() => import('./pages/Returns/Returns'));
const TermsConditions = lazy(() => import('./pages/TermsConditions/TermsConditions'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const News = lazy(() => import('./pages/News/News'));
const Sitemap = lazy(() => import('./pages/Sitemap/Sitemap'));

// Loading fallback component
const LoadingFallback = () => (
  <LoadingSpinner fullScreen message="Loading..." />
);

function App() {
  return (
    <ErrorBoundary name="Root">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CourierProvider>
            <AdminProvider>
              <CartProvider>
                <WishlistProvider>
                  <OrderProvider>
                    <ToastProvider>
                      <ComparisonProvider>
                        <div className="App">
                        <Header />
                        <ScrollToTop />
                        <main className="main-content">
                          <ErrorBoundary name="Routes">
                            <Suspense fallback={<LoadingFallback />}>
                              <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/shop" element={<Shop />} />
                                <Route path="/product/:id" element={<ProductDetail />} />
                                <Route path="/cart" element={<Cart />} />
                                <Route path="/checkout" element={<Checkout />} />
                                <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
                                <Route path="/category/:categoryName" element={<Category />} />
                                <Route path="/my-account" element={<Account />} />
                                <Route path="/compare" element={<Comparison />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/returns" element={<Returns />} />
                                <Route path="/terms-conditions" element={<TermsConditions />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/news" element={<News />} />
                                <Route path="/sitemap" element={<Sitemap />} />
                                <Route path="/admin" element={(
                                  <AdminRoute>
                                    <Admin />
                                  </AdminRoute>
                                )} />
                                <Route path="/courier-login" element={<CourierLogin />} />
                                <Route path="/courier-dashboard" element={(
                                  <CourierRoute>
                                    <CourierDashboard />
                                  </CourierRoute>
                                )} />
                                <Route path="/server-error" element={<ServerError />} />
                                <Route path="*" element={<NotFound />} />
                              </Routes>
                            </Suspense>
                          </ErrorBoundary>
                        </main>
                        <Footer />
                      </div>
                    </ComparisonProvider>
                  </ToastProvider>
                </OrderProvider>
              </WishlistProvider>
            </CartProvider>
          </AdminProvider>
          </CourierProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
